// The Conquer the Flag game screen, shared by the site's room and the CTF playground so both look and
// behave the same: the left column is the detail (whose turn, actions, the last roll and why, the unit
// or cell being looked at), the center is the board, the right column is the summary (teams, units,
// outposts, log). It brings its own stylesheet (CSS below), so it renders the same in both hosts.
// The host supplies the game, who controls which team, and how to send an action; selection,
// click-to-walk and the unit card live here.

import React, { useEffect, useRef, useState } from 'react';
import {
  actorTeam, playerOf, listLegalActions, cellCounts, isLegal, unitLabel, unitById, EFFECT_NAMES, effectText, turnOffset, armyOf, ABILITY_NAMES,
} from '../../../games/capture-the-flag/capture-the-flag';
import type { AbilityId, CtfAction, CtfState, Hex, Team } from '../../../games/capture-the-flag/types';
import CtfBoard from './CtfBoard';
import type { HexHint } from './CtfBoard';
import TurnIcon from './TurnIcon';
import {
  actionLabel, buttonRank, cellInfo, decisionText, hexKey, lastTagOf, playView, resultText, unitInfo, lastRoll, tagOffers, peekHints, unitName,
} from './play';

const TEAMS: Team[] = ['A', 'B'];

export interface CtfScreenProps {
  game: CtfState;
  names: Record<Team, string>;
  controls: (t: Team) => boolean;          // this viewer gives the orders for team t
  isBot: (t: Team) => boolean;             // team t is played by a bot
  onAction: (a: CtfAction, step?: boolean) => void;   // apply and send one action (step: a later step of a click-to-walk path)
  undo?: { enabled: boolean; run: () => void };
  statusNote?: string;                     // added to the status line (" · paused")
  turnExtra?: React.ReactNode;             // under the turn panel (bot speed, playground controls)
  endExtra?: React.ReactNode;              // under the result when the game is over
  summaryExtra?: React.ReactNode;          // bottom of the right column (rules, replay)
  audience?: string[];                     // people watching (spectators), listed in the summary
  onResign?: (t: Team) => void;            // offered for a team this viewer plays, at the bottom of the right column
  unitType?: (t: Team, name: string) => string | undefined;   // a nicknamed unit's type (league armies), shown in the unit panel
  setup?: { state: CtfState; hints: Map<string, HexHint>; onHexClick: (h: Hex) => void; panel: React.ReactNode };   // playground set-up mode
}

// Army abilities still open to a team: rerolls left, and this round's Pass / Sprint / Rally.
function abilityText(g: CtfState, t: Team): string {
  const id = g.config.abilities?.[t], ar = armyOf(g, t);
  const jailed = g.config.prisons ? g.units.filter(u => u.team === t && u.inPrison).length : 0;
  const pre = jailed ? ` · ${jailed} in prison` : '';
  if (!id) return pre;
  return pre + abilityPart(id, ar);
}
function abilityPart(id: AbilityId, ar: ReturnType<typeof armyOf>): string {
  if (id === 'reroll') return ` · ${ar.rerolls} reroll${ar.rerolls === 1 ? '' : 's'} left`;
  const used = id === 'pass' ? ar.passUsed : id === 'sprint' ? ar.sprintUsed : ar.rallyUsed;
  return ` · ${ABILITY_NAMES[id]} ${used ? 'used this round' : 'ready'}`;
}

export default function CtfGameScreen(p: CtfScreenProps) {
  const g = p.game;
  const [pick, setPick] = useState<{ id: string; at: CtfState } | null>(null);   // own unit selected, not yet activated
  const [card, setCard] = useState<{ unit?: string; cell?: number } | null>(null);   // what the unit card shows
  const walk = useRef<CtfAction[]>([]);   // the rest of a click-to-walk path
  const [confirmResign, setConfirmResign] = useState(false);

  // Click-to-walk: keep stepping along the chosen path while it is still this unit's move.
  useEffect(() => {
    if (!walk.current.length) return;
    const next = walk.current[0];
    if (g.pending || !g.active || !isLegal(g, next)) { walk.current = []; return; }
    const timer = window.setTimeout(() => { walk.current = walk.current.slice(1); p.onAction(next, true); }, 110);
    return () => window.clearTimeout(timer);
  });

  const actor = actorTeam(g);
  const myTurn = !p.setup && g.phase === 'playing' && !!actor && p.controls(actor) && !p.isBot(actor);
  const pid = actor ? playerOf(g, actor).id : null;
  const legal: CtfAction[] = myTurn && pid ? listLegalActions(g, pid) : [];
  const view = playView(g, legal, myTurn ? pid : null, pick && pick.at === g ? pick.id : null);

  // The unit being looked at: the card's unit, else the one selected or activating.
  const looked = card?.unit ?? view.preview ?? g.active?.unitId ?? null;
  const hints = new Map<string, HexHint>(view.hints);
  if (card?.cell !== undefined && !card.unit && !view.preview) g.cells[card.cell].hexes.forEach(h => { if (!hints.has(hexKey(h))) hints.set(hexKey(h), { kind: 'area' }); });
  if (card?.unit && card.unit !== view.preview && card.unit !== g.active?.unitId) {
    try { peekHints(g, card.unit).forEach((hint, k) => { if (!hints.has(k)) hints.set(k, hint); }); } catch { /* unit gone */ }
  }

  const run = (actions: CtfAction[]) => {
    setPick(null);
    setCard(null);
    walk.current = actions.slice(1);
    p.onAction(actions[0]);
  };
  const press = (a: CtfAction) => {
    const r = view.press(a);
    if (r.actions?.length) run(r.actions);
    else if (r.preview !== undefined) setPick(r.preview ? { id: r.preview, at: g } : null);
  };
  const onHexClick = (h: Hex) => {
    if (p.setup) { p.setup.onHexClick(h); return; }
    const r = view.click(h);
    if (r.actions?.length) { run(r.actions); return; }
    if (r.preview !== undefined) setPick(r.preview ? { id: r.preview, at: g } : null);
    const unit = r.preview ?? r.unit;
    const same = card && card.cell === r.cell && card.unit === unit;
    setCard(r.cell !== undefined && !same ? { cell: r.cell, unit } : r.preview ? { unit: r.preview } : null);
  };

  const nameOf = (t: Team) => p.names[t];
  const status = (() => {
    if (p.setup) return 'Setting up a position';
    if (g.phase === 'complete') return resultText(g, nameOf);
    if (!actor) return '';
    const who = myTurn ? `${nameOf(actor)} (you)` : p.isBot(actor) ? `${nameOf(actor)} (bot)` : nameOf(actor);
    if (view.preview) return `${who}: ${unitName(g, view.preview)} selected${g.pending?.kind === 'blink' ? ' to blink' : ''}`;
    return `${who}: ${decisionText(g)}${p.statusNote ?? ''}`;
  })();
  const hint = !myTurn ? null
    : view.note ? view.note
    : g.pending?.kind === 'blink' ? (view.preview ? `Click a dot to blink ${unitName(g, view.preview)} there, or another of your units.` : 'Blink: click one of your units (dashed ring), then the dot where it should land.')
    : view.preview ? `Nothing is locked in yet: click a dot to move ${unitName(g, view.preview)} there, a red ring to tag, or another unit to look at it.`
    : g.active ? 'Click a dot to move there (small dots take several steps; the unit turns on the way). Red rings are tag targets.'
    : 'Click one of your units with a dashed ring to see where it can go. Click any unit or cell to see what it is.';

  const offers = tagOffers(view.shown, view.buttons);
  // Resign: for the team you play (when you play both, the one to act).
  const resignTeam: Team | null = !p.onResign || p.setup || g.phase !== 'playing' ? null
    : actor && p.controls(actor) && !p.isBot(actor) ? actor
    : TEAMS.find(t => p.controls(t) && !p.isBot(t)) ?? null;
  // Who starts the next round (the first team alternates each round), and whether the round's last
  // activation (the one being taken or chosen now) runs straight into it: that side acts twice in a row.
  const nextFirst: Team = g.firstTeam === 'A' ? 'B' : 'A';
  const left = TEAMS.reduce((n, t) => n + g.activations[t].allowed - g.activations[t].used, 0);
  const roundEnd = !p.setup && g.phase === 'playing' && !!actor && left === 1
    ? (actor === nextFirst
      ? `${nameOf(actor)}'s last activation this round, and ${nameOf(actor)} goes first next round: two in a row.`
      : `Last activation this round. ${nameOf(nextFirst)} goes first next round.`)
    : null;
  const roll = lastRoll(g);
  const cells = cellCounts(g);
  const recentLog = g.log.filter(e => e.kind !== 'move' && e.kind !== 'turn').slice(-30).reverse();

  return (
    <div className="ctfg">
      <style>{CSS}</style>
      <div className="ctfg-grid">
        {/* Left: the detail */}
        <aside className="ctfg-left">
          {p.setup ? p.setup.panel : (
            <>
              <section className={`ctfg-panel ctfg-turn ${actor && g.phase === 'playing' ? `ctfg-turn-${actor}` : ''}`}>
                {(() => {
                  const mine = TEAMS.filter(t => p.controls(t) && !p.isBot(t));
                  return mine.length === 1 && !p.setup ? <div className={`ctfg-you ctfg-${mine[0]}`}>You are {mine[0] === 'A' ? 'Blue' : 'Red'}</div> : null;
                })()}
                <div className="ctfg-status" data-testid="ctf-status">{status}</div>
                {roundEnd && <p className="ctfg-roundend" role="status">{roundEnd}</p>}
                {myTurn && view.buttons.length > 0 && (() => {
                  // Turns get their own row, first, so they stay put while the other buttons (tags,
                  // guard…) come and go as the zone swings: turning three times can't hit a tag.
                  const btn = (a: CtfAction, i: number) => (
                    <button key={i} type="button" className={`ctfg-btn ${buttonRank(a) === 2 ? 'ctfg-quiet' : 'ctfg-act'}${a.type === 'turn' ? ' ctfg-icon' : ''}`} onClick={() => press(a)}
                      title={a.type === 'turn' ? actionLabel(view.shown, a, view.preview) : undefined}
                      aria-label={a.type === 'turn' ? actionLabel(view.shown, a, view.preview) : undefined}>
                      {a.type === 'turn' ? <TurnIcon clockwise={a.dir === -1} /> : actionLabel(view.shown, a, view.preview)}
                    </button>
                  );
                  const turns = view.buttons.filter(a => a.type === 'turn');
                  const rest = view.buttons.filter(a => a.type !== 'turn');
                  return (
                    <>
                      {turns.length > 0 && <div className="ctfg-actions">{turns.map(btn)}</div>}
                      {rest.length > 0 && <div className="ctfg-actions">{rest.map(btn)}</div>}
                    </>
                  );
                })()}
                {offers.length > 0 && (
                  <ul className="ctfg-offers" aria-label="Tag odds">
                    {offers.map((o, i) => <li key={i}><b>{o.target}: {o.need}+</b> <span>{o.why}</span></li>)}
                  </ul>
                )}
                {hint && <p className="ctfg-hint">{hint}</p>}
                {(p.undo || p.turnExtra) && (
                  <div className="ctfg-row">
                    {p.undo && <button type="button" className="ctfg-btn ctfg-quiet" disabled={!p.undo.enabled} onClick={p.undo.run}>Undo</button>}
                    {p.turnExtra}
                  </div>
                )}
                {g.phase === 'complete' && p.endExtra && <div className="ctfg-row">{p.endExtra}</div>}
              </section>

              <section className="ctfg-panel ctfg-roll" aria-label="Last roll">
                <h2>Last roll</h2>
                {roll ? (
                  <>
                    <div className="ctfg-roll-main">
                      <span className={`ctfg-die ${roll.hit ? 'hit' : 'miss'}`} aria-label={`Rolled ${roll.total}`}>{roll.total}</span>
                      <div>
                        <div className="ctfg-roll-res">{roll.hit ? 'Hit' : 'Miss'} · needed {roll.need}+</div>
                        <div className="ctfg-muted">{roll.attacker} {roll.longShot ? 'long shot at' : 'tagged'} {roll.target}</div>
                      </div>
                    </div>
                    {roll.why && <p className="ctfg-why">{roll.why}</p>}
                  </>
                ) : <p className="ctfg-muted">No tags yet. Tags roll a d8 and need 5+ before modifiers.</p>}
              </section>

              <UnitCard g={g} unit={looked} cell={card?.unit ? undefined : card?.cell} names={p.names} onClose={card ? () => setCard(null) : undefined} unitType={p.unitType} />
            </>
          )}
        </aside>

        {/* Center: the board */}
        <main className={`ctfg-board ${p.setup ? 'ctfg-editing' : ''}`}>
          <CtfBoard state={p.setup ? p.setup.state : view.shown} hints={p.setup ? p.setup.hints : hints} onHexClick={onHexClick}
            lastTag={p.setup ? null : lastTagOf(g)} clickAll={!!p.setup} focus={p.setup ? null : looked} />
        </main>

        {/* Right: the summary */}
        <aside className="ctfg-right">
          <section className="ctfg-panel">
            <div className="ctfg-teams">
              <span className="ctfg-A">{p.names.A}</span><span className="ctfg-muted">vs</span><span className="ctfg-B">{p.names.B}</span>
            </div>
            {p.audience && p.audience.length > 0 && <p className="ctfg-muted ctfg-small" style={{ margin: '6px 0 0' }}>Watching: {p.audience.join(', ')}</p>}
            <div className="ctfg-chips">
              <span>First flag home wins</span>
              <span>Round {g.round}{g.config.mercyRounds > 0 ? ` of ${g.config.mercyRounds}` : ''}</span>
              <span>Cells <i className="ctfg-A">{cells.A}</i>–<i className="ctfg-B">{cells.B}</i>{g.config.dominationCells > 0 ? ` · win at ${g.config.dominationCells}` : ''}</span>
            </div>
          </section>

          <section className="ctfg-panel">
            <h2>Units</h2>
            {TEAMS.map(t => (
              <div key={t} className="ctfg-unitrow">
                {g.units.filter(u => u.team === t).map(u => (
                  <button key={u.id} type="button" onClick={() => setCard({ unit: u.id })} title={`${unitName(g, u.id)} · ${unitInfo(g, u.id).name}`}
                    className={`ctfg-unit ctfg-u-${t} ${u.activated && u.id !== g.active?.unitId ? 'done' : ''} ${u.id === g.active?.unitId ? 'on' : ''} ${u.id === looked ? 'looked' : ''} ${u.pos ? '' : 'out'}`}>
                    {unitLabel(g, u)}{u.carrying && <em>F</em>}
                  </button>
                ))}
                <span className="ctfg-muted ctfg-small">{g.activations[t].used}/{g.activations[t].allowed} activated{abilityText(g, t)}</span>
              </div>
            ))}
            {/* Turn order: the activations left this round, in order (the sides alternate; one that runs out
                leaves the other to play the rest in a row), then the next round's first. Two pips of one
                color either side of the line are a side acting twice in a row. */}
            {g.phase === 'playing' && !p.setup && (() => {
              const order: Team[] = [];
              const rest = { A: g.activations.A.allowed - g.activations.A.used, B: g.activations.B.allowed - g.activations.B.used };
              for (let t: Team = g.turn; rest.A + rest.B > 0; t = t === 'A' ? 'B' : 'A') if (rest[t] > 0) { order.push(t); rest[t]--; }
              const twice = order[order.length - 1] === nextFirst;
              return (
                <div className="ctfg-order" aria-label="Turn order">
                  <h2 className="ctfg-gap">Turn order</h2>
                  <div className="ctfg-pips">
                    {order.map((t, i) => <span key={i} className={`ctfg-pip ctfg-pip-${t}${i === 0 ? ' now' : ''}`} title={`${nameOf(t)}${i === 0 ? ' (now)' : ''}`} />)}
                    <span className="ctfg-pipline" aria-hidden="true"><i />R{g.round + 1}</span>
                    <span className={`ctfg-pip ctfg-pip-${nextFirst} next`} title={`${nameOf(nextFirst)} goes first in round ${g.round + 1}`}>1st</span>
                  </div>
                  <p className={`ctfg-small ${twice ? 'ctfg-amber' : 'ctfg-muted'}`} style={{ margin: '4px 0 0' }}>
                    {twice
                      ? (order.length === 1 ? `${nameOf(nextFirst)} now, then ${nameOf(nextFirst)} again to start round ${g.round + 1}: two in a row.`
                        : `${nameOf(nextFirst)} acts last this round and first in round ${g.round + 1}: two in a row.`)
                      : `${nameOf(nextFirst)} goes first in round ${g.round + 1}.`}
                  </p>
                </div>
              );
            })()}
            {g.effects.some(Boolean) && (
              <>
                <h2 className="ctfg-gap">Outposts</h2>
                <ul className="ctfg-outposts">
                  {g.effects.map((fx, id) => fx && (
                    <li key={id} title={effectText(g.config)[fx]}>
                      <button type="button" onClick={() => setCard({ cell: id })}>{EFFECT_NAMES[fx]}</button>
                      <span className="ctfg-muted">cell {id}</span>
                      <span className={g.owners[id] ? `ctfg-${g.owners[id]}` : 'ctfg-muted'}>{g.owners[id] ? nameOf(g.owners[id]!) : 'neutral'}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
            <p className="ctfg-legend ctfg-small"><b>Board:</b> dashed outlines are guard zones that stop movement; small diamonds are zones that only tag. Dots show where the selected unit can go; hollow dots, where a unit you are looking at could go.</p>
          </section>

          <section className="ctfg-panel">
            <h2>Log</h2>
            <ol className="ctfg-log">
              {recentLog.map((e, i) => <li key={i} className={e.team ? `ctfg-lt-${e.team}` : 'ctfg-lt-none'}>{e.text}</li>)}
            </ol>
          </section>
          {p.summaryExtra}
          {resignTeam && (
            <section className="ctfg-panel" aria-label="Resign">
              {!confirmResign
                ? <div className="ctfg-row" style={{ marginTop: 0 }}><button type="button" className="ctfg-btn ctfg-quiet" onClick={() => setConfirmResign(true)}>Resign this game</button></div>
                : <div className="ctfg-row ctfg-confirm" style={{ marginTop: 0 }} role="group" aria-label="Confirm resigning">
                    <span>{nameOf(resignTeam)} resigns and {nameOf(resignTeam === 'A' ? 'B' : 'A')} wins?</span>
                    <button type="button" className="ctfg-btn ctfg-act" onClick={() => { setConfirmResign(false); p.onResign!(resignTeam); }}>Resign</button>
                    <button type="button" className="ctfg-btn ctfg-quiet" onClick={() => setConfirmResign(false)}>Keep playing</button>
                  </div>}
            </section>
          )}
        </aside>
      </div>
    </div>
  );
}

// The card for the unit (and cell) being looked at: its name, Move, a picture of its guard zone
// (the unit facing up, as in the team editor) and its traits.
function UnitCard({ g, unit, cell, names, onClose, unitType }: { g: CtfState; unit: string | null; cell?: number; names: Record<Team, string>; onClose?: () => void; unitType?: (t: Team, name: string) => string | undefined }) {
  const u = unit ? (() => { try { return unitInfo(g, unit); } catch { return null; } })() : null;
  const c = cell !== undefined ? cellInfo(g, cell) : null;
  const label = unit && u ? unitLabel(g, unitById(g, unit)) : '';
  return (
    <section className="ctfg-panel ctfg-card" aria-label="Unit">
      <div className="ctfg-card-head">
        <h2>{u ? 'Unit' : c ? 'Cell' : 'Unit'}</h2>
        {onClose && <button type="button" className="ctfg-x" onClick={onClose} aria-label="Close">×</button>}
      </div>
      {u && (
        <>
          <div className={`ctfg-card-title ctfg-${u.team}`}>{u.name}{(() => { const t = unitType?.(u.team, u.name); return t && t !== u.name ? <span className="ctfg-muted" style={{ fontWeight: 400 }}> ({t})</span> : null; })()}</div>
          <div className="ctfg-card-move">Move {u.move}{u.moveNote && <span className="ctfg-muted"> ({u.moveNote})</span>}</div>
          <ZonePicture zone={u.zone} team={u.team} label={label} stops={u.stops} />
          <p className="ctfg-muted ctfg-small">{u.stops === 'never' ? 'Tags in its zone; never stops enemies.'
            : u.stops === 'always' ? 'Tags in its zone; stops enemies that move in, all round.'
            : 'Tags in its zone; stops enemies that move in until it activates (or all round on guard).'}</p>
          {u.traits.length > 0 && (
            <ul className="ctfg-traits">{u.traits.map(t => <li key={t.name}><b>{t.name}</b> <span className="ctfg-muted">{t.text}</span></li>)}</ul>
          )}
          <p className="ctfg-muted ctfg-small">{u.status}</p>
        </>
      )}
      {c && (
        <>
          <div className="ctfg-card-title">{c.title} <span className={c.owner ? `ctfg-${c.owner}` : 'ctfg-muted'}>{c.owner ? names[c.owner] : 'neutral'}</span></div>
          <p>{c.effect ?? 'No outpost.'}</p>
          {c.prison && <p>{c.prison}</p>}
        </>
      )}
      {!u && !c && <p className="ctfg-muted">Click any unit, yours or the enemy's, to see its Move, guard zone and traits, and where it can go. Click a cell to see what it is.</p>}
    </section>
  );
}

// A unit's guard zone around it, the unit facing up (zones are stored facing direction 0; up is 2).
const ZH = 18;
export function ZonePicture({ zone, team, label, stops }: { zone: [number, number][]; team: Team; label: string; stops: 'until' | 'always' | 'never' }) {
  const shown = new Set(zone.map(o => turnOffset(o, 2).join(',')));
  const reach = Math.max(1, ...zone.map(([q, r]) => (Math.abs(q) + Math.abs(r) + Math.abs(q + r)) / 2));
  const at = (q: number, r: number) => ({ x: 1.5 * q * ZH, y: Math.sqrt(3) * (r + q / 2) * ZH });
  const pts = (x: number, y: number, k = 0.92) => [0, 1, 2, 3, 4, 5].map(i => `${(x + Math.cos(Math.PI / 3 * i) * ZH * k).toFixed(1)},${(y + Math.sin(Math.PI / 3 * i) * ZH * k).toFixed(1)}`).join(' ');
  const color = team === 'A' ? '#5b9bf0' : '#ef6b5b';
  const hexes: JSX.Element[] = [];
  for (let q = -reach; q <= reach; q++) for (let r = -reach; r <= reach; r++) {
    const d = (Math.abs(q) + Math.abs(r) + Math.abs(q + r)) / 2;
    if (d > reach) continue;
    const { x, y } = at(q, r);
    const on = shown.has(`${q},${r}`);
    if (d === 0) {
      hexes.push(<g key="self"><polygon points={pts(x, y)} fill={color} /><text x={x} y={y + 1} textAnchor="middle" dominantBaseline="central" fontSize={13} fontWeight={700} fill="#fff">{label || '•'}</text>
        <path d={`M ${x - 6} ${y - 10} L ${x} ${y - 16} L ${x + 6} ${y - 10} Z`} fill="#fff" /></g>);
      continue;
    }
    hexes.push(<g key={`${q},${r}`}>
      <polygon points={pts(x, y)} fill={on ? color : 'none'} fillOpacity={on ? (stops === 'never' ? 0.12 : 0.35) : 1} stroke={on ? color : 'var(--line)'}
        strokeWidth={on ? 1.6 : 1} strokeDasharray={on && stops !== 'never' ? '3 2' : undefined} />
      {on && stops === 'never' && <polygon points={`${x},${y - 4} ${x + 4},${y} ${x},${y + 4} ${x - 4},${y}`} fill="none" stroke={color} strokeWidth={1.4} />}
    </g>);
  }
  const w = (reach * 1.5 + 1) * ZH * 2 + 4, h = (reach * 2 + 1) * Math.sqrt(3) * ZH + 4;
  return <svg className="ctfg-zone" viewBox={`${-w / 2} ${-h / 2} ${w} ${h}`} width={w} height={h} role="img" aria-label={`Guard zone: ${zone.length} hex${zone.length === 1 ? '' : 'es'}`}>{hexes}</svg>;
}

const CSS = `
.ctfg { --bg: #0e1413; --surface: #161e1d; --sunk: #0b100f; --raise: #1f2927; --ink: #e4ebe8; --muted: #93a19c; --line: #2b3735;
  --a: #5b9bf0; --b: #ef6b5b; --amber: #f2b441; --amber-ink: #1b1405; --hit: #6fd3a4; --miss: #ff8a7a;
  background: var(--bg); color: var(--ink); font: 14px/1.45 "IBM Plex Sans", "Segoe UI", system-ui, sans-serif;
  flex: 1; }   /* fills the page below the room's bar (no light strip under a short board) */
.ctfg * { box-sizing: border-box; }
.ctfg h2 { font-size: 11.5px; font-weight: 600; letter-spacing: .08em; text-transform: uppercase; color: var(--muted); margin: 0 0 8px; }
.ctfg-grid { max-width: 1600px; margin: 0 auto; padding: 12px; display: grid; gap: 12px; align-items: start;
  grid-template-columns: 300px minmax(0, 1fr) 300px; grid-template-areas: "left board right"; }
.ctfg-left { grid-area: left; } .ctfg-board { grid-area: board; } .ctfg-right { grid-area: right; }
@media (max-width: 1180px) { .ctfg-grid { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); grid-template-areas: "board board" "left right"; } }
@media (max-width: 720px) { .ctfg-grid { grid-template-columns: minmax(0, 1fr); grid-template-areas: "board" "left" "right"; padding: 8px; } }
.ctfg-left, .ctfg-right { display: grid; gap: 10px; min-width: 0; }
.ctfg-panel { background: var(--surface); border: 1px solid var(--line); border-radius: 10px; padding: 12px; min-width: 0; }
.ctfg-board { background: var(--surface); border: 1px solid var(--line); border-radius: 10px; padding: 6px; min-width: 0; position: sticky; top: 8px; }
.ctfg-board svg { display: block; width: 100%; height: auto; max-height: calc(100vh - 40px); margin: 0 auto; }
@media (max-width: 1180px) { .ctfg-board { position: static; } }
.ctfg-editing { border-color: var(--amber); box-shadow: 0 0 0 1px var(--amber) inset; }
.ctfg-A { color: var(--a); } .ctfg-B { color: var(--b); }
.ctfg-muted { color: var(--muted); } .ctfg-small { font-size: 12px; }
.ctfg-turn { border-left-width: 4px; } .ctfg-turn-A { border-left-color: var(--a); } .ctfg-turn-B { border-left-color: var(--b); }
.ctfg-status { font-weight: 600; font-size: 16px; }
.ctfg-you { font-size: 12px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; margin-bottom: 2px; }
.ctfg-actions { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 10px; }
.ctfg-btn { font: inherit; color: inherit; background: var(--raise); border: 1px solid var(--line); border-radius: 6px; padding: 5px 10px; min-height: 34px; cursor: pointer; }
.ctfg-btn:hover:not(:disabled) { border-color: var(--muted); }
.ctfg-btn:disabled { opacity: .4; cursor: default; }
.ctfg-act { border-color: var(--amber); color: var(--amber); background: color-mix(in srgb, var(--amber) 10%, var(--surface)); }
.ctfg-quiet { color: var(--muted); }
.ctfg-icon { display: inline-flex; align-items: center; justify-content: center; min-width: 42px; }
.ctfg-offers { list-style: none; margin: 10px 0 0; padding: 0; display: grid; gap: 4px; font-size: 12.5px; color: var(--muted); }
.ctfg-offers b { color: var(--ink); }
.ctfg-hint { color: var(--muted); font-size: 12.5px; margin: 10px 0 0; }
.ctfg-pips { display: flex; flex-wrap: wrap; align-items: center; gap: 4px; row-gap: 8px; }
.ctfg-pip { width: 14px; height: 14px; border-radius: 999px; display: inline-flex; align-items: center; justify-content: center; font-size: 10px; font-weight: 800; color: #1b1405; }
.ctfg-pip-A { background: var(--a); } .ctfg-pip-B { background: var(--b); }
.ctfg-pip.now { box-shadow: 0 0 0 2px var(--surface), 0 0 0 3.5px var(--amber); margin: 0 2px; }
.ctfg-pip.next { width: 26px; height: 16px; font-size: 9.5px; outline: 2px dashed var(--amber); outline-offset: 2px; margin-left: 2px; }
.ctfg-pipline { display: inline-flex; flex-direction: column; align-items: center; margin: 0 2px; color: var(--muted); font-size: 10px; font-weight: 600; line-height: 1; }
.ctfg-pipline i { width: 1px; height: 12px; background: var(--muted); margin-bottom: 2px; }
.ctfg-amber { color: var(--amber); }
.ctfg-roundend { margin: 6px 0 0; padding: 5px 8px; border-radius: 6px; background: rgba(242, 180, 65, .14); color: var(--amber); font-size: 12.5px; font-weight: 600; }
.ctfg-row { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; margin-top: 10px; }
.ctfg-confirm { padding: 8px; border: 1px solid var(--amber); border-radius: 8px; }
.ctfg-roll-main { display: flex; align-items: center; gap: 12px; }
.ctfg-die { width: 52px; height: 52px; border-radius: 10px; display: grid; place-items: center; font: 700 28px/1 ui-monospace, Menlo, monospace; border: 2px solid; flex: none; }
.ctfg-die.hit { color: var(--hit); border-color: var(--hit); background: color-mix(in srgb, var(--hit) 12%, var(--surface)); }
.ctfg-die.miss { color: var(--miss); border-color: var(--miss); background: color-mix(in srgb, var(--miss) 10%, var(--surface)); }
.ctfg-roll-res { font-weight: 600; font-size: 15px; }
.ctfg-why { margin: 8px 0 0; font-size: 12.5px; color: var(--muted); }
.ctfg-card-head { display: flex; align-items: center; justify-content: space-between; }
.ctfg-x { font: inherit; color: var(--muted); background: none; border: 0; cursor: pointer; font-size: 18px; line-height: 1; padding: 0 4px; }
.ctfg-card-title { font-weight: 600; font-size: 15px; }
.ctfg-card-move { font-weight: 600; margin-top: 2px; }
.ctfg-zone { display: block; margin: 10px auto 6px; max-width: 100%; height: auto; }
.ctfg-traits { list-style: none; margin: 8px 0 0; padding: 0; display: grid; gap: 4px; }
.ctfg-card p { margin: 6px 0 0; }
.ctfg-teams { display: flex; gap: 8px; align-items: baseline; font-weight: 600; font-size: 16px; }
.ctfg-chips { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
.ctfg-chips span { font-size: 12px; padding: 2px 8px; border-radius: 999px; background: var(--raise); color: var(--muted); }
.ctfg-chips i { font-style: normal; font-weight: 600; }
.ctfg-unitrow { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; margin-bottom: 6px; }
.ctfg-unit { position: relative; width: 32px; height: 32px; padding: 0; border-radius: 50%; display: grid; place-items: center; font: 700 13px system-ui, sans-serif; color: #fff; border: 0; cursor: pointer; }
.ctfg-u-A { background: #3b82f6; } .ctfg-u-B { background: #ef4444; }
.ctfg-unit.done { opacity: .45; } .ctfg-unit.out { opacity: .25; outline: 1px dashed var(--muted); }
.ctfg-unit.on { box-shadow: 0 0 0 2px var(--amber); } .ctfg-unit.looked { outline: 2px solid var(--ink); outline-offset: 2px; }
.ctfg-unit em { position: absolute; top: -4px; right: -5px; font: 700 9px/1 ui-monospace, monospace; background: var(--amber); color: var(--amber-ink); padding: 2px 3px; border-radius: 3px; }
.ctfg-gap { margin-top: 12px !important; }
.ctfg-outposts { list-style: none; margin: 0; padding: 0; display: grid; gap: 4px; font-size: 13px; }
.ctfg-outposts li { display: grid; grid-template-columns: 1fr auto auto; gap: 8px; }
.ctfg-outposts button { font: inherit; color: inherit; background: none; border: 0; padding: 0; text-align: left; cursor: pointer; text-decoration: underline dotted; }
.ctfg-legend { margin: 10px 0 0; color: var(--muted); }
.ctfg-log { list-style: none; margin: 0; padding: 0; display: grid; gap: 4px; max-height: 320px; overflow-y: auto; font-size: 13px; }
.ctfg-log li { padding-left: 8px; border-left: 2px solid var(--line); }
.ctfg-lt-A { border-left-color: var(--a) !important; } .ctfg-lt-B { border-left-color: var(--b) !important; } .ctfg-lt-none { color: var(--muted); }
.ctfg button:focus-visible { outline: 2px solid var(--amber); outline-offset: 2px; }
`;
